import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Heading, Text, VStack, List, ListItem, Link, Divider, useColorModeValue } from '@chakra-ui/react'

export default function HowTo() {
	const muted = useColorModeValue('gray.600', 'bgg.muted')
	const linkColor = useColorModeValue('brand.600', 'orange.200')

	return (
		<VStack spacing={8} align="stretch" maxW="lg">
			<VStack spacing={2} align="flex-start">
				<Heading as="h1" size="lg">
					How to use this app
				</Heading>
				<Text color={muted} fontSize="md" lineHeight="tall">
					Quick notes for anyone pulling data from{' '}
					<Link href="https://boardgamegeek.com" isExternal color={linkColor}>
						BoardGameGeek
					</Link>
					. Nothing here replaces BGG—think of these pages as lightweight helpers on top of your public collection.
				</Text>
			</VStack>

			<section>
				<Heading as="h2" size="md" mb={3}>
					What you need on BGG
				</Heading>
				<VStack align="stretch" spacing={3}>
					<Text color={muted} fontSize="sm" lineHeight="tall">
						On each game’s BGG page, use <strong>Collection</strong> to add it to your profile (stay logged in on BGG).
					</Text>
					<Text color={muted} fontSize="sm" lineHeight="tall">
						Only games marked <strong>Own</strong> are fetched here. For bulk edits and statuses, BGG’s{' '}
						<Link href="https://boardgamegeek.com/wiki/page/Collection_Manager" isExternal color={linkColor}>
							Collection Manager
						</Link>{' '}
						wiki is the right place to start.
					</Text>
				</VStack>
			</section>

			<Divider />

			<section>
				<Heading as="h2" size="md" mb={2}>
					<Link as={RouterLink} to="/game-picker" color={linkColor}>
						Picker
					</Link>
				</Heading>
				<Text color={muted} fontSize="sm" mb={3} lineHeight="tall">
					Enter one or more BGG usernames (comma-separated), load the owned set, then roll for a single pick from whatever survives your filters. You get a bit of flair (strip / flip) around the draw.
				</Text>
				<Text fontSize="xs" fontWeight="600" color={muted} textTransform="uppercase" letterSpacing="0.05em" mb={2}>
					Filters
				</Text>
				<List spacing={1.5} fontSize="sm" color={muted}>
					<ListItem>• Your rating floor, optional BGG rating, player count, and toggles for rated / played / with comment.</ListItem>
					<ListItem>• Optional hide for expansions and a max listed playing-time cap.</ListItem>
					<ListItem>• Everything lives behind the filter accordion so the main view stays calm.</ListItem>
				</List>
			</section>

			<Divider />

			<section>
				<Heading as="h2" size="md" mb={2}>
					<Link as={RouterLink} to="/ranking-engine" color={linkColor}>
						Ranking engine
					</Link>
				</Heading>
				<Text color={muted} fontSize="sm" mb={3} lineHeight="tall">
					Load the same kind of owned list, then drag games between tiers (S through F), keep a pool in <strong>Unsorted</strong>, or park titles in <strong>Excluded</strong> so they stay out of the final table.
				</Text>
				<Text fontSize="xs" fontWeight="600" color={muted} textTransform="uppercase" letterSpacing="0.05em" mb={2}>
					Filters
				</Text>
				<Text color={muted} fontSize="sm" mb={3}>
					Same knobs as Picker: ratings, player counts, comment/rated/played flags, expansions, max time.
				</Text>
				<Text fontSize="xs" fontWeight="600" color={muted} textTransform="uppercase" letterSpacing="0.05em" mb={2}>
					Save &amp; share
				</Text>
				<List spacing={1.5} fontSize="sm" color={muted}>
					<ListItem>
						• <strong>Saved slots</strong> — up to twelve named snapshots in this browser’s localStorage; open <strong>Saved slots</strong> while loading the collection (step 1) or use the panel on the tier screen (steps 2–3).
					</ListItem>
					<ListItem>
						• <strong>Export JSON</strong> (schema v2) for a portable backup; bring it back through step 0 <strong>Import</strong>.
					</ListItem>
					<ListItem>• <strong>Download JPG</strong> for a shareable image of the tier overview.</ListItem>
				</List>
			</section>

			<Divider />

			<section>
				<Heading as="h2" size="md" mb={2}>
					<Link as={RouterLink} to="/pick-5" color={linkColor}>
						Pick 5
					</Link>
				</Heading>
				<Text color={muted} fontSize="sm" lineHeight="tall">
					Same fetch-and-filter story as Picker, but the app draws five candidates at once so you can compare covers side by side and commit to one winner—handy when a single random roll feels too blunt.
				</Text>
			</section>
		</VStack>
	)
}
